import { useForm } from "@inertiajs/react";
import { MessageSquare } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierName: string;
  quoteId: number;
  initialNotes?: string | null;
  onSaved?: () => void;
}

export function SupplierObservationsDialog({
  open,
  onOpenChange,
  supplierName,
  quoteId,
  initialNotes,
  onSaved,
}: Props) {
  const routes = useOrganizationRoutes();
  const { data, setData, patch, processing } = useForm<{
    quote: { buyer_notes: string };
  }>({
    quote: { buyer_notes: initialNotes ?? "" },
  });

  const handleSave = () => {
    patch(
      routes.quotes.show(quoteId),
      {
        preserveScroll: true,
        onSuccess: () => {
          onOpenChange(false);
          onSaved?.();
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Observaciones para {supplierName}
          </DialogTitle>
          <DialogDescription>
            Estas observaciones aparecerán en el PDF de la orden de compra exportada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 flex-1 min-h-0">
          <Textarea
            value={data.quote.buyer_notes}
            onChange={(e) =>
              setData("quote", {
                ...data.quote,
                buyer_notes: e.target.value,
              })
            }
            placeholder="Escribe aquí las observaciones que quieres incluir en la orden de compra para este proveedor..."
            className="h-[200px] max-h-[40vh] resize-none overflow-y-auto whitespace-pre-wrap"
          />
          <p className="text-xs text-muted-foreground">
            Ejemplo: &quot;Favor coordinar entrega con 48hs de anticipación&quot; o &quot;Materiales deben cumplir especificación técnica X&quot;
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={processing}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={processing}>
            {processing ? "Guardando..." : "Guardar observaciones"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
